// SCR-STAFF-1003 — عامل‌های محلی و تجهیزات (US-INT-060 تا 064؛ AP-INT-03): ثبت ایستگاه کاری و کد جفت‌سازی یک‌بارمصرف ۱۰ دقیقه‌ای
// (AC-INT-060.1)، وضعیت، آخرین اتصال، نسخه و تجهیزات (AC-INT-064.1)، نگاشت تجهیز به نقش‌های ایستگاه (AC-INT-060.2)، پنجره به‌روزرسانی،
// آزمون چاپ (AC-INT-061.1) و آزمون کارت‌خوان فقط با شبیه‌ساز و نشان «نمونه» (AC-INT-062.1، CLAUDE.md §10)، تجدید کد و ابطال با دلیل.
import AddIcon from '@mui/icons-material/Add';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type AgentDeviceRole,
  type AgentDeviceView,
  type AgentStatus,
  type AgentTestResultView,
  type LocalAgentPairingView,
  type LocalAgentView,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  FormDialog,
  JalaliDate,
  KeyValueList,
  ListPage,
  ReasonDialog,
  SampleBadge,
  StatusChip,
  type DataTableColumn,
  type StatusTone,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { WriteNotice } from '../settings/common';
import { intKeys } from './common';

export const SCR_STAFF_1003 = 'SCR-STAFF-1003';

/** نقش‌های ایستگاه کاری (AC-INT-060.2) به ترتیب نمایش؛ نوع تجهیز مجاز هر نقش. */
export const AGENT_ROLES: readonly { role: AgentDeviceRole; kind: AgentDeviceView['kind']; capability: string }[] = [
  { role: 'ReceiptPrinter', kind: 'Printer', capability: 'escpos' },
  { role: 'A4Printer', kind: 'Printer', capability: 'pdf' },
  { role: 'LabelPrinter', kind: 'Printer', capability: 'escpos' },
  { role: 'CardReader', kind: 'CardReader', capability: 'purchase' },
];

const STATUS_TONE: Record<AgentStatus, StatusTone> = {
  PendingPairing: 'pending',
  Online: 'active',
  Offline: 'error',
  Updating: 'pending',
  Revoked: 'inactive',
};

/** مقایسه نسخه‌های عددی نقطه‌دار (۱.۲.۳)؛ نسخه نامعتبر ← قدیمی‌تر نیست. */
export function isOlderVersion(current: string | null | undefined, latest: string | null | undefined): boolean {
  if (!current || !latest) {
    return false;
  }
  const parse = (value: string) => value.split('.').map((part) => Number.parseInt(part, 10));
  const a = parse(current);
  const b = parse(latest);
  if ([...a, ...b].some((part) => Number.isNaN(part))) {
    return false;
  }
  for (let index = 0; index < Math.max(a.length, b.length); index += 1) {
    const left = a[index] ?? 0;
    const right = b[index] ?? 0;
    if (left !== right) {
      return left < right;
    }
  }
  return false;
}

/** ساعت HH:mm از TimeOnly سرور (HH:mm:ss). */
const toHourMinute = (value: string) => value.slice(0, 5);

export function AgentStatusChip({ status }: { status: AgentStatus }) {
  return (
    <span data-testid="agent-status" data-agent-status={status}>
      <StatusChip tone={STATUS_TONE[status]} labelKey={`int:agents.status.${status}`} />
    </span>
  );
}

export function LocalAgentsPage() {
  const { t } = useTranslation('int');
  const { tenantApi, session, activeTenant } = useTenantSession();
  const write = useWriteAccess('int.agent.manage');
  const queryClient = useQueryClient();
  const [branchFilter, setBranchFilter] = useState('');
  const [creating, setCreating] = useState(false);
  const [pairing, setPairing] = useState<LocalAgentPairingView | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<LocalAgentView | null>(null);

  const agents = useQuery({
    queryKey: [...intKeys.agents(session?.tenantId), branchFilter],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/integration/agents', {
          params: { query: branchFilter ? { branchId: branchFilter } : {} },
        }),
      ),
    // AC-INT-064.1: داشبورد وضعیت زنده (Heartbeat هر ۳۰ ثانیه).
    refetchInterval: 30_000,
  });
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: intKeys.agents(session?.tenantId) });

  const reissue = useMutation({
    mutationFn: (agent: LocalAgentView) =>
      unwrap(
        tenantApi.POST('/api/v1/integration/agents/{id}:reissue-code', { params: { path: { id: agent.id } } }),
      ),
    onSuccess: (view) => {
      setPairing(view);
      invalidate();
    },
  });
  const revoke = useMutation({
    mutationFn: ({ agent, reason }: { agent: LocalAgentView; reason: string }) =>
      unwrap(
        tenantApi.POST('/api/v1/integration/agents/{id}:revoke', {
          params: { path: { id: agent.id } },
          body: { reason },
        }),
      ),
    onSuccess: () => {
      setRevoking(null);
      invalidate();
    },
  });

  const branchName = (id: string) => activeTenant?.branches.find((branch) => branch.branchId === id)?.name ?? '—';
  const latestVersion = agents.data?.latestVersion ?? null;
  const selected = agents.data?.items.find((agent) => agent.id === selectedId) ?? null;

  const columns: DataTableColumn<LocalAgentView>[] = [
    {
      id: 'workstation',
      headerKey: 'int:agents.columns.workstation',
      cell: (row) => (
        <Stack spacing={0.25}>
          <span>{row.workstationName}</span>
          {row.machineName ? (
            <Typography variant="caption" color="text.secondary">
              <bdi dir="ltr">{row.machineName}</bdi>
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    { id: 'branch', headerKey: 'int:agents.columns.branch', cell: (row) => branchName(row.branchId) },
    {
      id: 'status',
      headerKey: 'int:agents.columns.status',
      cell: (row) => (
        <span data-testid={`agent-row-status-${row.id}`} data-status={row.status}>
          <AgentStatusChip status={row.status} />
        </span>
      ),
    },
    {
      id: 'last_seen',
      headerKey: 'int:agents.columns.last_seen',
      cell: (row) => {
        const seen = row.lastHeartbeatAt ?? row.lastConnectedAt;
        return seen ? <JalaliDate value={seen} withTime /> : <span>{t('int:agents.never_connected')}</span>;
      },
    },
    {
      id: 'version',
      headerKey: 'int:agents.columns.version',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <bdi dir="ltr">{row.agentVersion ?? '—'}</bdi>
          {isOlderVersion(row.agentVersion, latestVersion) ? (
            <Chip
              size="small"
              color="info"
              variant="outlined"
              label={t('int:agents.update_pending', { version: latestVersion })}
              data-testid={`agent-update-pending-${row.id}`}
            />
          ) : null}
        </Stack>
      ),
    },
    {
      id: 'devices',
      headerKey: 'int:agents.columns.devices',
      cell: (row) => (
        <span data-testid={`agent-devices-${row.id}`}>
          {faDigits(row.devices.filter((device) => device.isPresent).length)}
        </span>
      ),
    },
    {
      id: 'actions',
      headerKey: 'int:agents.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('int:agents.details')}>
            <IconButton
              aria-label={`${t('int:agents.details')} ${row.workstationName}`}
              onClick={() => setSelectedId(row.id)}
              data-testid={`agent-details-${row.id}`}
            >
              <SettingsOutlinedIcon />
            </IconButton>
          </Tooltip>
          {row.status === 'PendingPairing' ? (
            <Tooltip title={t('int:agents.reissue')}>
              <span>
                <IconButton
                  aria-label={`${t('int:agents.reissue')} ${row.workstationName}`}
                  onClick={() => reissue.mutate(row)}
                  disabled={!write.canWrite || reissue.isPending}
                  data-testid={`agent-reissue-${row.id}`}
                >
                  <KeyOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          {row.status !== 'Revoked' ? (
            <Tooltip title={t('int:agents.revoke')}>
              <span>
                <IconButton
                  aria-label={`${t('int:agents.revoke')} ${row.workstationName}`}
                  onClick={() => setRevoking(row)}
                  disabled={!write.canWrite}
                  data-testid={`agent-revoke-${row.id}`}
                >
                  <BlockOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
        </Stack>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_1003}
      titleKey="int:agents.title"
      subtitleKey="int:agents.subtitle"
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreating(true)}
          disabled={!write.canWrite}
          data-testid="agent-create"
        >
          {t('int:agents.add')}
        </Button>
      }
      filters={
        <Stack spacing={1}>
          <TextField
            select
            size="small"
            label={t('int:agents.filter_branch')}
            value={branchFilter}
            onChange={(event) => setBranchFilter(event.target.value)}
            sx={{ maxWidth: 260 }}
            slotProps={{ htmlInput: { 'data-testid': 'agent-branch-filter' } }}
          >
            <MenuItem value="">{t('int:agents.all_branches')}</MenuItem>
            {(activeTenant?.branches ?? []).map((branch) => (
              <MenuItem key={branch.branchId} value={branch.branchId}>
                {branch.name}
              </MenuItem>
            ))}
          </TextField>
          <WriteNotice write={write} />
          <Alert severity="info">{t('int:agents.browser_fallback_hint')}</Alert>
          {latestVersion ? (
            <Typography variant="body2" color="text.secondary" data-testid="agent-latest-version">
              {t('int:agents.latest_version', { version: latestVersion })}
            </Typography>
          ) : null}
          {reissue.error ? <ErrorState error={reissue.error} compact /> : null}
        </Stack>
      }
    >
      <DataTable
        captionKey="int:agents.caption"
        columns={columns}
        rows={agents.data?.items}
        getRowId={(row) => row.id}
        isLoading={agents.isPending}
        error={agents.error}
        onRetry={() => void agents.refetch()}
        emptyTitleKey="int:agents.empty"
      />
      {creating ? (
        <CreateAgentDialog
          onClose={() => setCreating(false)}
          onCreated={(view) => {
            setCreating(false);
            setPairing(view);
            invalidate();
          }}
        />
      ) : null}
      {pairing ? <PairingCodeDialog pairing={pairing} onClose={() => setPairing(null)} /> : null}
      {selected ? (
        <AgentDetailsDialog agent={selected} canWrite={write.canWrite} onClose={() => setSelectedId(null)} onChanged={invalidate} />
      ) : null}
      <ReasonDialog
        open={revoking !== null}
        titleKey="int:agents.revoke_title"
        messageKey="int:agents.revoke_message"
        confirmKey="int:agents.revoke"
        maxLength={500}
        busy={revoke.isPending}
        error={revoke.error}
        onConfirm={(reason) => revoking && revoke.mutate({ agent: revoking, reason })}
        onCancel={() => {
          setRevoking(null);
          revoke.reset();
        }}
      />
    </ListPage>
  );
}

function CreateAgentDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (view: LocalAgentPairingView) => void;
}) {
  const { t } = useTranslation('int');
  const { tenantApi, activeTenant } = useTenantSession();
  const idempotency = useIdempotencyKey();
  const branches = activeTenant?.branches ?? [];
  const [branchId, setBranchId] = useState(branches[0]?.branchId ?? '');
  const [name, setName] = useState('');
  const [dirty, setDirty] = useState(false);
  const valid = branchId !== '' && name.trim().length > 0 && name.trim().length <= 100;

  const create = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/integration/agents', {
          body: { branchId, workstationName: name.trim() },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: onCreated,
  });

  return (
    <FormDialog
      open
      titleKey="int:agents.create_title"
      screenId={SCR_STAFF_1003}
      onSubmit={() => {
        if (valid) {
          create.mutate();
        }
      }}
      onClose={onClose}
      isDirty={dirty}
      isSubmitting={create.isPending}
      submitError={create.error}
    >
      <TextField
        select
        required
        label={t('int:agents.branch')}
        value={branchId}
        onChange={(event) => {
          setBranchId(event.target.value);
          setDirty(true);
        }}
        slotProps={{ htmlInput: { 'data-testid': 'agent-branch' } }}
      >
        {branches.map((branch) => (
          <MenuItem key={branch.branchId} value={branch.branchId}>
            {branch.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        required
        label={t('int:agents.workstation_name')}
        helperText={t('int:agents.workstation_hint')}
        value={name}
        onChange={(event) => {
          setName(event.target.value);
          setDirty(true);
        }}
        slotProps={{ htmlInput: { maxLength: 100, 'data-testid': 'agent-workstation-name' } }}
      />
    </FormDialog>
  );
}

/** کد جفت‌سازی فقط همین یک بار نمایش داده می‌شود (BR-INT-060)؛ بستن ← دیگر قابل بازیابی نیست (فقط تجدید کد). */
function PairingCodeDialog({ pairing, onClose }: { pairing: LocalAgentPairingView; onClose: () => void }) {
  const { t } = useTranslation('int');
  const titleId = useId();
  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth data-screen-id={SCR_STAFF_1003}>
      <DialogTitle id={titleId}>{t('int:agents.pairing_title', { name: pairing.agent.workstationName })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography
            component="p"
            variant="h4"
            dir="ltr"
            sx={{ fontFamily: 'monospace', letterSpacing: '0.3em', textAlign: 'center' }}
            data-testid="agent-pairing-code"
          >
            {pairing.pairingCode}
          </Typography>
          <Typography variant="body2" data-testid="agent-pairing-expires">
            {t('int:agents.pairing_expires')} <JalaliDate value={pairing.expiresAt} withTime />
          </Typography>
          <Alert severity="info">
            <Stack component="ol" spacing={0.5} sx={{ m: 0, ps: 2 }}>
              <li>{t('int:agents.pairing_step_install')}</li>
              <li>
                {t('int:agents.pairing_step_command')}{' '}
                <bdi dir="ltr" data-testid="agent-pairing-command">{`"C:\\Program Files\\Phoenix Local Agent\\Phoenix.LocalAgent.exe" pair ${pairing.pairingCode}`}</bdi>
              </li>
              <li>{t('int:agents.pairing_step_wait')}</li>
            </Stack>
          </Alert>
          <Alert severity="warning">{t('int:agents.pairing_once')}</Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="agent-pairing-close">
          {t('common:actions.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AgentDetailsDialog({
  agent,
  canWrite,
  onClose,
  onChanged,
}: {
  agent: LocalAgentView;
  canWrite: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation('int');
  const { tenantApi } = useTenantSession();
  const titleId = useId();
  const editable = canWrite && agent.status !== 'Revoked';
  const initialRoles = Object.fromEntries(
    AGENT_ROLES.map(({ role }) => [role, agent.roles.find((item) => item.role === role)?.deviceId ?? '']),
  ) as Record<AgentDeviceRole, string>;
  const [roles, setRoles] = useState(initialRoles);
  const [rolesDirty, setRolesDirty] = useState(false);
  const [windowStart, setWindowStart] = useState(toHourMinute(agent.updateWindowStart));
  const [windowEnd, setWindowEnd] = useState(toHourMinute(agent.updateWindowEnd));
  const [workstationName, setWorkstationName] = useState(agent.workstationName);
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [testResult, setTestResult] = useState<{ role: AgentDeviceRole; result: AgentTestResultView } | null>(null);

  const saveRoles = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/integration/agents/{id}/device-roles', {
          params: { path: { id: agent.id } },
          body: {
            roles: AGENT_ROLES.map(({ role }) => ({ role, deviceId: roles[role] || null })),
            rowVersion: agent.rowVersion,
          },
        }),
      ),
    onSuccess: () => {
      setRolesDirty(false);
      onChanged();
    },
  });
  const saveSettings = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/integration/agents/{id}', {
          params: { path: { id: agent.id } },
          body: {
            workstationName: workstationName.trim(),
            updateWindowStart: `${windowStart}:00`,
            updateWindowEnd: `${windowEnd}:00`,
            rowVersion: agent.rowVersion,
          },
        }),
      ),
    onSuccess: () => {
      setSettingsDirty(false);
      onChanged();
    },
  });
  const testPrint = useMutation({
    mutationFn: (role: AgentDeviceRole) =>
      unwrap(
        tenantApi.POST('/api/v1/integration/agents/{id}:test-print', {
          params: { path: { id: agent.id } },
          // متن آزمون از i18n (≤ ۱۰ سطر، هر سطر ≤ ۶۴ نویسه).
          body: { role, lines: [t('int:agents.test_print_line1'), agent.workstationName.slice(0, 64), t('int:agents.test_print_line2')] },
        }),
      ).then((result) => ({ role, result })),
    onSuccess: setTestResult,
  });
  const testCardReader = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/integration/agents/{id}:test-card-reader', {
          params: { path: { id: agent.id } },
          body: { amountMinor: null },
        }),
      ).then((result) => ({ role: 'CardReader' as AgentDeviceRole, result })),
    onSuccess: setTestResult,
  });

  const deviceFor = (id: string) => agent.devices.find((device) => device.id === id);
  const devicesFor = (kind: AgentDeviceView['kind'], capability: string) =>
    agent.devices.filter((device) => device.kind === kind && device.capabilities.includes(capability));
  const windowValid = /^\d{2}:\d{2}$/.test(windowStart) && /^\d{2}:\d{2}$/.test(windowEnd) && windowStart !== windowEnd;
  const settingsValid = windowValid && workstationName.trim().length > 0 && workstationName.trim().length <= 100;

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby={titleId}
      maxWidth="md"
      fullWidth
      data-screen-id={SCR_STAFF_1003}
      data-testid="agent-details"
    >
      <DialogTitle id={titleId}>
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{agent.workstationName}</span>
          <AgentStatusChip status={agent.status} />
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <KeyValueList
            items={[
              { labelKey: 'int:agents.fields.machine', value: agent.machineName ? <bdi dir="ltr">{agent.machineName}</bdi> : null },
              { labelKey: 'int:agents.fields.os', value: agent.osDescription ? <bdi dir="ltr">{agent.osDescription}</bdi> : null },
              { labelKey: 'int:agents.fields.version', value: agent.agentVersion ? <bdi dir="ltr">{agent.agentVersion}</bdi> : null },
              { labelKey: 'int:agents.fields.paired_at', value: agent.pairedAt ? <JalaliDate value={agent.pairedAt} withTime /> : null },
              {
                labelKey: 'int:agents.fields.last_heartbeat',
                value: agent.lastHeartbeatAt ? <JalaliDate value={agent.lastHeartbeatAt} withTime /> : null,
                testId: 'agent-last-heartbeat',
              },
              {
                labelKey: 'int:agents.fields.last_disconnected',
                value: agent.lastDisconnectedAt ? <JalaliDate value={agent.lastDisconnectedAt} withTime /> : null,
              },
              {
                labelKey: 'int:agents.fields.certificate_expires',
                value: agent.certificateExpiresAt ? <JalaliDate value={agent.certificateExpiresAt} /> : null,
              },
              { labelKey: 'int:agents.fields.update_target', value: agent.updateTargetVersion ? <bdi dir="ltr">{agent.updateTargetVersion}</bdi> : null },
              { labelKey: 'int:agents.fields.revoke_reason', value: agent.revokeReason },
            ]}
          />

          <section aria-labelledby={`${titleId}-devices`}>
            <Typography id={`${titleId}-devices`} variant="subtitle1" gutterBottom>
              {t('int:agents.devices_title')}
            </Typography>
            {agent.devices.length === 0 ? (
              <Typography color="text.secondary" data-testid="agent-no-devices">
                {t('int:agents.no_devices')}
              </Typography>
            ) : (
              <Stack spacing={1} component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {agent.devices.map((device) => (
                  <Stack
                    key={device.id}
                    component="li"
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ flexWrap: 'wrap' }}
                    data-testid={`agent-device-${device.deviceKey}`}
                  >
                    <bdi dir="ltr">{device.name}</bdi>
                    <Chip size="small" variant="outlined" label={t(`int:agents.kind.${device.kind}`)} />
                    <Typography variant="caption" color="text.secondary">
                      <bdi dir="ltr">{device.driver}</bdi>
                    </Typography>
                    {device.isSimulator ? <SampleBadge /> : null}
                    {!device.isPresent ? (
                      <Chip size="small" color="warning" variant="outlined" label={t('int:agents.device_absent')} />
                    ) : null}
                  </Stack>
                ))}
              </Stack>
            )}
          </section>

          <section aria-labelledby={`${titleId}-roles`}>
            <Typography id={`${titleId}-roles`} variant="subtitle1" gutterBottom>
              {t('int:agents.roles_title')}
            </Typography>
            <Stack spacing={2}>
              {AGENT_ROLES.map(({ role, kind, capability }) => {
                const mapped = roles[role] ? deviceFor(roles[role]) : undefined;
                const candidates = devicesFor(kind, capability);
                return (
                  <Stack key={role} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                    <TextField
                      select
                      size="small"
                      label={t(`int:agents.role.${role}`)}
                      value={roles[role]}
                      onChange={(event) => {
                        setRoles({ ...roles, [role]: event.target.value });
                        setRolesDirty(true);
                      }}
                      disabled={!editable}
                      sx={{ minWidth: 260 }}
                      slotProps={{ htmlInput: { 'data-testid': `agent-role-${role}` } }}
                    >
                      <MenuItem value="">{t('int:agents.role_unassigned')}</MenuItem>
                      {candidates.map((device) => (
                        <MenuItem key={device.id} value={device.id}>
                          {device.isSimulator ? `${device.name} (${t('common:sample.badge')})` : device.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    {role === 'CardReader' ? (
                      <Button
                        size="small"
                        onClick={() => testCardReader.mutate()}
                        disabled={!editable || rolesDirty || !mapped?.isSimulator || agent.status !== 'Online' || testCardReader.isPending}
                        data-testid="agent-test-card-reader"
                      >
                        {t('int:agents.test_card_reader')}
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        onClick={() => testPrint.mutate(role)}
                        disabled={!editable || rolesDirty || !mapped || agent.status !== 'Online' || testPrint.isPending}
                        data-testid={`agent-test-print-${role}`}
                      >
                        {t('int:agents.test_print')}
                      </Button>
                    )}
                  </Stack>
                );
              })}
              <Typography variant="caption" color="text.secondary">
                {t('int:agents.card_reader_hint')}
              </Typography>
              {saveRoles.error ? <ErrorState error={saveRoles.error} compact /> : null}
              {testPrint.error ? <ErrorState error={testPrint.error} compact /> : null}
              {testCardReader.error ? <ErrorState error={testCardReader.error} compact /> : null}
              {testResult ? <TestResultAlert value={testResult} onClose={() => setTestResult(null)} /> : null}
              <div>
                <Button
                  variant="outlined"
                  onClick={() => saveRoles.mutate()}
                  disabled={!editable || !rolesDirty || saveRoles.isPending}
                  data-testid="agent-roles-save"
                >
                  {t('int:agents.roles_save')}
                </Button>
              </div>
            </Stack>
          </section>

          <section aria-labelledby={`${titleId}-settings`}>
            <Typography id={`${titleId}-settings`} variant="subtitle1" gutterBottom>
              {t('int:agents.settings_title')}
            </Typography>
            <Stack spacing={2}>
              <TextField
                size="small"
                label={t('int:agents.workstation_name')}
                value={workstationName}
                onChange={(event) => {
                  setWorkstationName(event.target.value);
                  setSettingsDirty(true);
                }}
                disabled={!editable}
                slotProps={{ htmlInput: { maxLength: 100, 'data-testid': 'agent-edit-name' } }}
              />
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  type="time"
                  label={t('int:agents.window_start')}
                  value={windowStart}
                  onChange={(event) => {
                    setWindowStart(event.target.value);
                    setSettingsDirty(true);
                  }}
                  disabled={!editable}
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 300, dir: 'ltr', 'data-testid': 'agent-window-start' } }}
                />
                <TextField
                  size="small"
                  type="time"
                  label={t('int:agents.window_end')}
                  value={windowEnd}
                  onChange={(event) => {
                    setWindowEnd(event.target.value);
                    setSettingsDirty(true);
                  }}
                  disabled={!editable}
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 300, dir: 'ltr', 'data-testid': 'agent-window-end' } }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {windowValid ? t('int:agents.window_hint') : t('int:agents.window_invalid')}
              </Typography>
              {saveSettings.error ? <ErrorState error={saveSettings.error} compact /> : null}
              <div>
                <Button
                  variant="outlined"
                  onClick={() => saveSettings.mutate()}
                  disabled={!editable || !settingsDirty || !settingsValid || saveSettings.isPending}
                  data-testid="agent-settings-save"
                >
                  {t('int:agents.settings_save')}
                </Button>
              </div>
            </Stack>
          </section>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="agent-details-close">
          {t('common:actions.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TestResultAlert({
  value,
  onClose,
}: {
  value: { role: AgentDeviceRole; result: AgentTestResultView };
  onClose: () => void;
}) {
  const { t } = useTranslation('int');
  const { result } = value;
  // Unknown هرگز موفق نیست (BR-BIL-034): هشدار جداگانه برای پیگیری دستی.
  const severity = result.success ? 'success' : result.status === 'Unknown' ? 'warning' : 'error';
  return (
    <Alert severity={severity} onClose={onClose} data-testid="agent-test-result" data-status={result.status}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
        <span>
          {result.success
            ? value.role === 'CardReader'
              ? t('int:agents.test_card_ok', {
                  reference: result.referenceNumber ?? '—',
                  last4: result.cardLast4 ?? '—',
                })
              : t('int:agents.test_print_ok')
            : t(`int:agents.test_status.${result.status}`, {
                defaultValue: t('int:agents.test_failed'),
                code: result.errorCode ?? '—',
              })}
        </span>
        {result.errorCode && !result.success ? <bdi dir="ltr">{result.errorCode}</bdi> : null}
        {result.isSample ? <SampleBadge /> : null}
      </Stack>
    </Alert>
  );
}
